import React, { useState, useEffect } from "react";
import { FileText } from "lucide-react";
import { TakeoffService } from "../services/TakeoffService";
import { Modal, ModalBody, ModalFooter, Input, Dropdown, Button } from "./ui";

const TakeoffManagementModal = ({
  takeoff,
  isOpen,
  onClose,
  onSave,
  clients = [],
  theme = 'dark',
}) => {
  const [formData, setFormData] = useState({
    status: "",
    abCivilPrice: "",
    ufredoPrice: "",
    invoicedStatus: "",
    invoicedDate: "",
    paidDate: "",
    earthworksTechInvNumber: "",
    poNumber: "",
    paidToUfredo: "",
    paidToUfredoDate: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (takeoff && isOpen) {
      setFormData({
        status: takeoff.status || "",
        abCivilPrice: takeoff.abCivilPrice || "",
        ufredoPrice: takeoff.ufredoPrice || "",
        invoicedStatus: takeoff.invoicedStatus || "",
        invoicedDate: takeoff.invoicedDate || "",
        paidDate: takeoff.paidDate || "",
        earthworksTechInvNumber: takeoff.earthworksTechInvNumber || "",
        poNumber: takeoff.poNumber || "",
        paidToUfredo: takeoff.paidToUfredo || "",
        paidToUfredoDate: takeoff.paidToUfredoDate || "",
      });
    }
  }, [takeoff, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await TakeoffService.updateTakeoff(takeoff.id, formData);
      if (onSave) {
        await onSave();
      }
      onClose();
    } catch (err) {
      console.error("Error updating takeoff:", err);
      setError(err.message || "Failed to update takeoff");
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return 'Not Set';
    // Remove all non-digit characters
    const cleaned = String(phoneNumber).replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX for 10-digit numbers
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    // For 11-digit numbers (with country code), format as +1 (XXX) XXX-XXXX
    if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    // Return original if not standard format
    return phoneNumber;
  };

  if (!takeoff) return null;

  return (
    <Modal trackAs="TakeoffManagementModal" isOpen={isOpen} onClose={onClose} size="lg" theme={theme}>
      {/* Header with Icon */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--cta-blue)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FileText size={28} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {takeoff.projectName || 'Takeoff Details'}
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-2)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
              }}
            >
              Takeoff Project
            </p>
          </div>
        </div>
      </div>

      <ModalBody>
        {error && (
          <div style={{
            padding: 'var(--space-4)',
            marginBottom: 'var(--space-4)',
            background: 'var(--danger-bg, #dc26261a)',
            border: '1px solid var(--danger, #dc2626)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--danger, #dc2626)',
            fontSize: 'var(--text-sm)',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* Left Column - Project Info */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <h2 style={{
                margin: 0,
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-200)',
                paddingBottom: 'var(--space-2)',
                borderBottom: '1px solid var(--gray-700)',
              }}>
                Project Information
              </h2>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Job Number
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}>
                  {takeoff.jobNumber || 'Not Set'}
                </div>
              </div>

              <Dropdown
                label="Status"
                value={formData.status}
                onChange={(value) => handleInputChange("status", value)}
                options={[
                  { value: "", label: "Select Status" },
                  { value: "Not Started", label: "Not Started" },
                  { value: "In Progress", label: "In Progress" },
                  { value: "Completed", label: "Completed" },
                  { value: "On Hold", label: "On Hold" },
                  { value: "Cancelled", label: "Cancelled" },
                ]}
              />

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Client
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}>
                  {takeoff?.client?.name || takeoff?.formClient || 'Not Set'}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Contact Name
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}>
                  {takeoff?.formName || 'Not Set'}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Email
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}>
                  {takeoff?.email || 'Not Set'}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: 'var(--space-2)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--gray-300)'
                }}>
                  Phone
                </label>
                <div style={{
                  padding: 'var(--space-3)',
                  background: 'var(--dark-elevated)',
                  border: '1px solid var(--gray-600)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-300)',
                }}>
                  {formatPhoneNumber(takeoff?.formPhone)}
                </div>
              </div>
            </div>

            {/* Right Column - Invoice Information */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <h2 style={{
                margin: 0,
                fontSize: 'var(--text-lg)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-200)',
                paddingBottom: 'var(--space-2)',
                borderBottom: '1px solid var(--gray-700)',
              }}>
                Invoice Information
              </h2>

              <Input
                label="AB Civil Price"
                type="number"
                value={formData.abCivilPrice}
                onChange={(e) => handleInputChange("abCivilPrice", e.target.value)}
                placeholder="Enter AB Civil price"
                step="0.01"
                icon={<span>$</span>}
                iconPosition="left"
              />

              <Input
                label="Ufredo Price"
                type="number"
                value={formData.ufredoPrice}
                onChange={(e) => handleInputChange("ufredoPrice", e.target.value)}
                placeholder="Enter Ufredo price"
                step="0.01"
                icon={<span>$</span>}
                iconPosition="left"
              />

              <Input
                label="PO Number"
                type="text"
                value={formData.poNumber}
                onChange={(e) => handleInputChange("poNumber", e.target.value)}
                placeholder="Enter PO number"
              />

              <Dropdown
                label="Invoiced Status"
                value={formData.invoicedStatus}
                onChange={(value) => handleInputChange("invoicedStatus", value)}
                options={[
                  { value: "", label: "Select Status" },
                  { value: "Invoiced", label: "Invoiced" },
                  { value: "Paid", label: "Paid" },
                ]}
              />

              <Input
                label="Invoiced Date"
                type="date"
                value={formData.invoicedDate}
                onChange={(e) => handleInputChange("invoicedDate", e.target.value)}
              />

              <Input
                label="Paid Date"
                type="date"
                value={formData.paidDate}
                onChange={(e) => handleInputChange("paidDate", e.target.value)}
              />

              <Input
                label="Earthworks Tech Inv Number"
                type="text"
                value={formData.earthworksTechInvNumber}
                onChange={(e) => handleInputChange("earthworksTechInvNumber", e.target.value)}
                placeholder="Enter invoice number"
              />

              <Dropdown
                label="Paid to Ufredo"
                value={formData.paidToUfredo}
                onChange={(value) => handleInputChange("paidToUfredo", value)}
                options={[
                  { value: "", label: "Select Status" },
                  { value: "No", label: "No" },
                  { value: "Pending", label: "Pending" },
                  { value: "Paid", label: "Paid" },
                ]}
              />

              <Input
                label="Paid to Ufredo Date"
                type="date"
                value={formData.paidToUfredoDate}
                onChange={(e) => handleInputChange("paidToUfredoDate", e.target.value)}
              />
            </div>
          </div>
        </form>
      </ModalBody>

      <ModalFooter>
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          gap: 'var(--space-3)',
        }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={loading}>
            Save Changes
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default TakeoffManagementModal;
